import { useState, useEffect, useMemo, useRef } from "react";
import {
  Modal, ModalHeader, ModalBody, ModalFooter,
  Input, Label, Spinner, Button,
} from "reactstrap";
import Select from "react-select";
import FeatherIcon from "feather-icons-react";
import { toast } from "react-toastify";
import {
  createInvoice,
  updateInvoice,
  uploadFileToInvoice,
  removeInvoiceAttachment,
  getInvoiceSignedUrls,
  uploadStagingFile,
  getStagingSignedUrls,
  removeStagingObject,
  formatCurrency,
} from "./Api";
import { Previewer } from "../../../../components/DocumentPreviewer/Previewer";
import { getFileExtension, imageTypes, pdfTypes, officeTypes, videoTypes }
  from "../../../../components/DocumentPreviewer/utils";

const selectStyles = {
  control: (base, { isFocused, isDisabled }) => ({
    ...base,
    borderColor: isFocused ? "#6366F1" : "#E2E8F0",
    borderRadius: 8,
    minHeight: 38,
    fontSize: "0.83rem",
    boxShadow: isFocused ? "0 0 0 2px rgba(99,102,241,0.15)" : "none",
    backgroundColor: isDisabled ? "#f8f9fa" : base.backgroundColor,
    "&:hover": { borderColor: "#6366F1" },
  }),
  option: (base, { isSelected, isFocused }) => ({
    ...base,
    fontSize: "0.83rem",
    background: isSelected ? "#4F46E5" : isFocused ? "#EEF2FF" : "white",
    color: isSelected ? "white" : "#0F172A",
  }),
  menu: (base) => ({ ...base, borderRadius: 8, border: "1px solid #E2E8F0", zIndex: 9999 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const LABEL_STYLE = {
  fontSize: "0.72rem", fontWeight: 700, color: "#334155",
  textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 6,
};

const INPUT_STYLE = {
  borderColor: "#E2E8F0",
  borderRadius: 8,
  fontSize: "0.85rem",
  height: 38,
};

const getFileTypeFromKey = (keyOrName) => {
  const ext = getFileExtension(keyOrName).toLowerCase();
  if (pdfTypes.includes(ext)) return "pdf";
  if (imageTypes.includes(ext)) return "image";
  if (videoTypes.includes(ext)) return "video";
  if (officeTypes.includes(ext)) return "office";
  return "unknown";
};

const FilePreviewModal = ({ isOpen, toggle, signedUrl, fileKey }) => {
  const type = fileKey ? getFileTypeFromKey(fileKey) : "unknown";
  const fileName = fileKey ? fileKey.split("/").pop() : "Attachment";
  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="xl" style={{ zIndex: 12000 }}>
      <ModalHeader toggle={toggle} style={{ background: "#0F172A", borderBottom: "none", padding: "14px 20px" }}>
        <span style={{ color: "white", fontWeight: 700, fontSize: "0.9rem" }}>{fileName}</span>
      </ModalHeader>
      <ModalBody style={{ padding: 0, background: "#0F172A" }}>
        {signedUrl ? (
          <Previewer signedUrl={signedUrl} type={type} height="72vh" width="100%" />
        ) : (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "72vh" }}>
            <Spinner style={{ color: "#A5B4FC" }} />
          </div>
        )}
      </ModalBody>
      <ModalFooter style={{ background: "#0F172A", border: "none", padding: "12px 20px" }}>
        <Button color="secondary" onClick={toggle}>Close</Button>
        {signedUrl && (
          <a
            href={signedUrl}
            target="_blank"
            rel="noopener noreferrer"
            download={fileName}
            className="iv-btn iv-btn--primary"
            style={{ textDecoration: "none" }}
          >
            <FeatherIcon icon="download" size={14} style={{ marginRight: 4 }} />
            Download
          </a>
        )}
      </ModalFooter>
    </Modal>
  );
};

const InvoiceFormModal = ({
  isOpen,
  onClose,
  invoiceData,
  isNew,
  properties = [],
  vendors,
  prefilledPropertyId,
  prefilledBuildingName,
  prefilledPropertyLabel,
}) => {
  const initial = useMemo(() => ({
    title: "",
    vendorId: "",
    amount: "",
    properties: prefilledPropertyId
      ? [{ propertyId: prefilledPropertyId, buildingName: prefilledBuildingName || "" }]
      : [{ propertyId: "", buildingName: "" }],
    description: "",
    referenceNo: "",
  }), [prefilledPropertyId, prefilledBuildingName]);

  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const submitLock = useRef(false);
  const [createdId, setCreatedId] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [signedUrls, setSignedUrls] = useState({});
  const [uploading, setUploading] = useState(false);
  const [previewKey, setPreviewKey] = useState(null);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [previewPendingId, setPreviewPendingId] = useState(null);
  const fileInputRef = useRef(null);

  const cleanupStaged = (files) => {
    (files || []).forEach((f) => {
      if (f.key) removeStagingObject(f.key).catch(() => {});
    });
  };

  useEffect(() => {
    if (!isOpen) return;
    if (isNew) {
      setForm(initial);
      setCreatedId(null);
      setAttachments([]);
      setSignedUrls({});
      cleanupStaged(pendingFiles);
      setPendingFiles([]);
    } else if (invoiceData) {
      const existingRefs = Array.isArray(invoiceData.properties) && invoiceData.properties.length
        ? invoiceData.properties.map((r) => ({
            propertyId: r.propertyId || "",
            buildingName: r.buildingName || "",
          }))
        : [{ propertyId: prefilledPropertyId || "", buildingName: prefilledBuildingName || "" }];
      setForm({
        title: invoiceData.title || "",
        vendorId: invoiceData.vendorId || invoiceData.vendor?._id || "",
        amount: invoiceData.amount ?? "",
        properties: existingRefs,
        description: invoiceData.description || "",
        referenceNo: invoiceData.referenceNo || "",
      });
      setCreatedId(invoiceData._id);
      setAttachments(invoiceData.attachments || []);
    }
    setErrors({});
  }, [isOpen, isNew, invoiceData, initial, prefilledPropertyId, prefilledBuildingName]);

  useEffect(() => {
    if (!attachments.length) {
      setSignedUrls({});
      return;
    }
    const missing = attachments.filter((k) => !signedUrls[k]);
    if (!missing.length) return;
    (async () => {
      const urls = await getInvoiceSignedUrls(missing);
      setSignedUrls((prev) => ({ ...prev, ...urls }));
    })();
  }, [attachments]); // eslint-disable-line

  const set = (k) => (v) => setForm((p) => ({ ...p, [k]: v }));

  // ── Linked properties (multi) ─────────────────────────────────────────────
  const propertyOptions = useMemo(
    () => (properties || []).map((p) => ({ value: p._id, label: p.title || p.name || p._id })),
    [properties]
  );

  const buildingOptionsFor = (propertyId) => {
    const prop = (properties || []).find((p) => p._id === propertyId);
    return (prop?.building || [])
      .filter((b) => b?.buildingName)
      .map((b) => ({ value: b.buildingName, label: b.buildingName }));
  };

  const updateRow = (idx, patch) =>
    setForm((p) => ({
      ...p,
      properties: p.properties.map((r, i) => (i === idx ? { ...r, ...patch } : r)),
    }));

  const addRow = () =>
    setForm((p) => ({ ...p, properties: [...p.properties, { propertyId: "", buildingName: "" }] }));

  const removeRow = (idx) =>
    setForm((p) => ({
      ...p,
      properties: p.properties.length > 1 ? p.properties.filter((_, i) => i !== idx) : p.properties,
    }));

  // When a property is chosen and it has exactly one building, auto-select it.
  const onPickProperty = (idx, propertyId) => {
    const buildings = buildingOptionsFor(propertyId);
    updateRow(idx, {
      propertyId,
      buildingName: buildings.length === 1 ? buildings[0].value : "",
    });
  };

  // Vendor options (active vendors). When editing an invoice whose vendor is no
  // longer active, keep a synthetic option so the selection still shows.
  const vendorOpts = useMemo(() => {
    const list = (vendors || []).map((v) => ({
      value: v._id,
      label: v.name,
      categoryName: v.category?.name || "",
      categoryId: v.categoryId || v.category?._id || "",
    }));
    if (!isNew && invoiceData?.vendorId && !list.some((o) => o.value === invoiceData.vendorId)) {
      list.unshift({
        value: invoiceData.vendorId,
        label: `${invoiceData.vendorName || "Vendor"} (inactive)`,
        categoryName: invoiceData.category?.name || "",
        categoryId: invoiceData.categoryId || "",
      });
    }
    return list;
  }, [vendors, isNew, invoiceData]);

  const selectedVendor = vendorOpts.find((o) => o.value === form.vendorId) || null;
  const derivedCategoryName =
    selectedVendor?.categoryName || invoiceData?.category?.name || "";

  const validate = () => {
    const e = {};
    if (!form.title?.trim()) e.title = "Title is required";
    if (!form.vendorId) e.vendorId = "Vendor is required";
    if (form.amount === "" || Number.isNaN(Number(form.amount)) || Number(form.amount) < 0) {
      e.amount = "A valid amount is required";
    }
    const rows = form.properties || [];
    if (!rows.length || rows.some((r) => !r.propertyId)) {
      e.properties = "Select at least one property";
    } else if (rows.some((r) => !r.buildingName?.trim())) {
      e.properties = "Choose a building for every selected property";
    } else {
      const seen = new Set();
      for (const r of rows) {
        const key = `${r.propertyId}::${r.buildingName.trim().toLowerCase()}`;
        if (seen.has(key)) { e.properties = "Remove duplicate property/building rows"; break; }
        seen.add(key);
      }
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (submitLock.current) return;
    if (!validate()) {
      toast.error("Please fix the highlighted fields");
      return;
    }
    if (pendingFiles.some((p) => p.uploading)) {
      toast.warning("Please wait for the files to finish uploading.");
      return;
    }
    submitLock.current = true;
    setSaving(true);
    try {
      const stagedKeys = pendingFiles.map((p) => p.key).filter(Boolean);
      const payload = {
        title: form.title.trim(),
        vendorId: form.vendorId,
        amount: Number(form.amount),
        properties: form.properties.map((r) => ({
          propertyId: r.propertyId,
          buildingName: r.buildingName.trim(),
        })),
        description: form.description.trim(),
        referenceNo: form.referenceNo.trim(),
      };
      if (isNew && !createdId) {
        const res = await createInvoice({ ...payload, attachments: stagedKeys });
        const id = res?.data?._id;
        if (!id) {
          toast.error("Invoice was not created.");
          return;
        }
        setCreatedId(id);
        setPendingFiles([]);
        toast.success(
          stagedKeys.length
            ? `Invoice created with ${stagedKeys.length} attachment${stagedKeys.length !== 1 ? "s" : ""}.`
            : "Invoice created."
        );
        onClose(true);
      } else {
        const targetId = invoiceData?._id || createdId;
        await updateInvoice(targetId, payload);
        toast.success("Invoice updated.");
        onClose(true);
      }
    } catch {
      // toast handled in api helper
    } finally {
      setSaving(false);
      submitLock.current = false;
    }
  };

  const invoiceId = createdId || invoiceData?._id;

  const handleFiles = async (files) => {
    if (!files?.length) return;

    if (!invoiceId) {
      // New invoice: upload each file to a temporary staging area right away so it
      // gets a signed URL and previews exactly like a saved attachment. The keys
      // are adopted by the invoice on create.
      const toStage = Array.from(files).map((file) => ({
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        file,
        name: file.name,
        key: null,
        signedUrl: null,
        uploading: true,
        error: false,
      }));
      setPendingFiles((prev) => [...prev, ...toStage]);
      if (fileInputRef.current) fileInputRef.current.value = "";

      await Promise.all(
        toStage.map(async (pf) => {
          try {
            const key = await uploadStagingFile(pf.file);
            const urls = await getStagingSignedUrls([key]);
            setPendingFiles((prev) =>
              prev.map((p) =>
                p.id === pf.id ? { ...p, key, signedUrl: urls[key] || null, uploading: false } : p
              )
            );
          } catch (err) {
            console.error("staging upload failed", err);
            setPendingFiles((prev) =>
              prev.map((p) => (p.id === pf.id ? { ...p, uploading: false, error: true } : p))
            );
            toast.error(`Failed to upload ${pf.name}`);
          }
        })
      );
      return;
    }

    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        try {
          const key = await uploadFileToInvoice(invoiceId, file);
          setAttachments((prev) => [...prev, key]);
        } catch (err) {
          console.error("upload failed", err);
        }
      }
      toast.success("Files uploaded");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleRemovePending = (id) => {
    setPendingFiles((prev) => {
      const removed = prev.find((p) => p.id === id);
      if (removed?.key) removeStagingObject(removed.key).catch(() => {});
      return prev.filter((p) => p.id !== id);
    });
  };

  const openPendingPreview = (pf) => {
    if (pf?.signedUrl) setPreviewPendingId(pf.id);
  };

  const handleRemove = async (key) => {
    try {
      await removeInvoiceAttachment(invoiceId, key);
      setAttachments((prev) => prev.filter((k) => k !== key));
      toast.success("Attachment removed");
    } catch {
      // handled
    }
  };

  // Header context shows the first linked property (with a "+N more" hint when
  // the invoice spans several properties); the full editor lives in the body.
  const linkedCount = (form.properties || []).filter((r) => r.propertyId).length;
  const firstRow = (form.properties || []).find((r) => r.propertyId) || null;
  const firstPropLabel = firstRow
    ? propertyOptions.find((o) => o.value === firstRow.propertyId)?.label || ""
    : "";
  const headerPropertyLabel = firstPropLabel
    ? `${firstPropLabel}${linkedCount > 1 ? ` +${linkedCount - 1} more` : ""}`
    : (isNew ? prefilledPropertyLabel || "" : "");
  const headerBuildingName = linkedCount > 1 ? "" : (firstRow?.buildingName || "");

  const handleClose = (refresh) => {
    if (isNew && !createdId) cleanupStaged(pendingFiles);
    setPendingFiles([]);
    onClose(refresh);
  };

  return (
    <>
      <Modal isOpen={isOpen} toggle={() => handleClose(false)} size="lg" centered backdrop="static">
        <ModalHeader toggle={() => handleClose(false)} style={{ borderBottom: "1px solid #EEF2F7" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{
              width: 38, height: 38, borderRadius: 10, flexShrink: 0,
              background: "#EEF2FF", display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <FeatherIcon icon={isNew ? "plus-circle" : "edit-2"} size={19} color="#4F46E5" />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: "1.05rem", fontWeight: 700, color: "#0F172A", lineHeight: 1.2 }}>
                {isNew ? "New Invoice" : `Edit Invoice ${invoiceData?.invoiceNo ? `#${invoiceData.invoiceNo}` : ""}`}
              </div>
              {(headerPropertyLabel || headerBuildingName) ? (
                <div style={{
                  display: "flex", alignItems: "center", gap: 6, marginTop: 4,
                  fontSize: "0.76rem", color: "#475569", fontWeight: 600,
                }}>
                  <FeatherIcon icon="map-pin" size={12} color="#4F46E5" style={{ flexShrink: 0 }} />
                  <span style={{ color: "#0F172A" }}>{headerPropertyLabel || "—"}</span>
                  {headerBuildingName && (
                    <>
                      <span style={{ color: "#CBD5E1" }}>·</span>
                      <FeatherIcon icon="home" size={12} color="#64748B" style={{ flexShrink: 0 }} />
                      <span>{headerBuildingName}</span>
                    </>
                  )}
                </div>
              ) : (
                <div style={{ fontSize: "0.75rem", color: "#64748B", fontWeight: 500, marginTop: 2 }}>
                  {isNew ? "Fill in details and attach files in one go" : "Update invoice details"}
                </div>
              )}
            </div>
          </div>
        </ModalHeader>
        <ModalBody style={{ padding: "20px 24px" }}>
          <div className="row g-3">
            <div className="col-md-8">
              <Label style={LABEL_STYLE}>Invoice Title <span style={{ color: "#DC2626" }}>*</span></Label>
              <Input
                value={form.title}
                onChange={(e) => set("title")(e.target.value)}
                placeholder="e.g. February cleaning service"
                style={INPUT_STYLE}
              />
              {errors.title && <div style={{ color: "#DC2626", fontSize: "0.72rem", marginTop: 4 }}>{errors.title}</div>}
            </div>
            <div className="col-md-4">
              <Label style={LABEL_STYLE}>Invoice No.</Label>
              <Input
                value={form.referenceNo}
                onChange={(e) => set("referenceNo")(e.target.value)}
                placeholder="Vendor invoice #"
                style={INPUT_STYLE}
              />
            </div>

            <div className="col-md-5">
              <Label style={LABEL_STYLE}>Vendor <span style={{ color: "#DC2626" }}>*</span></Label>
              <Select
                options={vendorOpts}
                value={selectedVendor}
                onChange={(opt) => set("vendorId")(opt?.value || "")}
                styles={selectStyles}
                placeholder={vendorOpts.length ? "Choose vendor" : "No vendors yet — add one first"}
                isDisabled={vendorOpts.length === 0}
                menuPortalTarget={document.body} menuPosition="fixed"
              />
              {errors.vendorId && <div style={{ color: "#DC2626", fontSize: "0.72rem", marginTop: 4 }}>{errors.vendorId}</div>}
            </div>
            <div className="col-md-4">
              <Label style={LABEL_STYLE}>Category</Label>
              <Input
                value={derivedCategoryName || "—"}
                disabled
                style={INPUT_STYLE}
                title="Category is set automatically from the selected vendor"
              />
            </div>
            <div className="col-md-3">
              <Label style={LABEL_STYLE}>Amount <span style={{ color: "#DC2626" }}>*</span></Label>
              <Input
                type="number" step="0.01" min="0"
                value={form.amount}
                onChange={(e) => set("amount")(e.target.value)}
                placeholder="0.00"
                style={INPUT_STYLE}
              />
              {errors.amount
                ? <div style={{ color: "#DC2626", fontSize: "0.72rem", marginTop: 4 }}>{errors.amount}</div>
                : (form.amount !== "" && !Number.isNaN(Number(form.amount)) && (
                    <div style={{ marginTop: 4, fontSize: "0.7rem", color: "#64748B" }}>
                      {formatCurrency(Number(form.amount))}
                    </div>
                  ))}
            </div>

            {isNew && (
              <div className="col-12 d-none">
                <div style={{
                  display: "flex", alignItems: "center", gap: 8,
                  background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 8,
                  padding: "8px 12px", fontSize: "0.74rem", color: "#64748B",
                }}>
                  <FeatherIcon icon="info" size={14} color="#4F46E5" style={{ flexShrink: 0 }} />
                  <span>
                    The invoice is recorded as <strong style={{ color: "#334155" }}>Pending</strong> with
                    today as its submitted date. The due date is set automatically from your company's payment day.
                  </span>
                </div>
              </div>
            )}

            {/* Linked properties — one or more, each pinned to a building. */}
            <div className="col-12">
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                <Label style={{ ...LABEL_STYLE, marginBottom: 0 }}>
                  Linked Properties <span style={{ color: "#DC2626" }}>*</span>
                </Label>
                <button
                  type="button"
                  onClick={addRow}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 4,
                    background: "#EEF2FF", border: "1px solid #C7D2FE", color: "#4F46E5",
                    borderRadius: 7, padding: "4px 10px", fontSize: "0.74rem", fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  <FeatherIcon icon="plus" size={12} /> Add property
                </button>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {(form.properties || []).map((row, idx) => {
                  const buildingOpts = buildingOptionsFor(row.propertyId);
                  return (
                    <div key={idx} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <div style={{ flex: 1 }}>
                        <Select
                          options={propertyOptions}
                          value={propertyOptions.find((o) => o.value === row.propertyId) || null}
                          onChange={(opt) => onPickProperty(idx, opt?.value || "")}
                          styles={selectStyles}
                          placeholder="Select property"
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <Select
                          options={buildingOpts}
                          value={buildingOpts.find((o) => o.value === row.buildingName) || null}
                          onChange={(opt) => updateRow(idx, { buildingName: opt?.value || "" })}
                          styles={selectStyles}
                          placeholder={row.propertyId ? "Select building" : "Pick property first"}
                          isDisabled={!row.propertyId}
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => removeRow(idx)}
                        disabled={(form.properties || []).length <= 1}
                        title="Remove"
                        style={{
                          flexShrink: 0, width: 34, height: 34, borderRadius: 8,
                          border: "1px solid #FECDD3", background: "#FEF2F2",
                          color: "#DC2626", display: "flex", alignItems: "center", justifyContent: "center",
                          cursor: (form.properties || []).length <= 1 ? "not-allowed" : "pointer",
                          opacity: (form.properties || []).length <= 1 ? 0.5 : 1,
                        }}
                      >
                        <FeatherIcon icon="trash-2" size={14} />
                      </button>
                    </div>
                  );
                })}
              </div>
              {errors.properties && (
                <div style={{ color: "#DC2626", fontSize: "0.72rem", marginTop: 4 }}>{errors.properties}</div>
              )}
            </div>

            <div className="col-12">
              <Label style={LABEL_STYLE}>Description / Notes</Label>
              <Input
                type="textarea"
                rows={3}
                value={form.description}
                onChange={(e) => set("description")(e.target.value)}
                placeholder="Anything the team should know about this invoice…"
                style={{ ...INPUT_STYLE, height: "auto", padding: "10px 12px" }}
              />
            </div>

            {/* Attachments — staged before create, uploaded after. All document types allowed. */}
            <div className="col-12">
              <div style={{
                background: "#F8FAFC", border: "1px dashed #CBD5E1", borderRadius: 10,
                padding: 16, marginTop: 6,
              }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                  <div>
                    <div style={{ fontWeight: 700, color: "#0F172A", fontSize: "0.85rem" }}>
                      <FeatherIcon icon="paperclip" size={14} style={{ marginRight: 6, verticalAlign: "middle" }} />
                      Invoice Files & Attachments
                    </div>
                    <div style={{ fontSize: "0.72rem", color: "#64748B", marginTop: 2 }}>
                      Upload the original invoice PDF, photos, receipts, or supporting documents.
                    </div>
                  </div>
                  <div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      style={{ display: "none" }}
                      onChange={(e) => handleFiles(e.target.files)}
                    />
                    <button
                      type="button"
                      className="iv-btn iv-btn--primary"
                      disabled={uploading || saving}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      {uploading ? <Spinner size="sm" /> : <FeatherIcon icon={invoiceId ? "upload" : "plus"} size={14} />}
                      {invoiceId ? "Upload" : "Add Files"}
                    </button>
                  </div>
                </div>
                {attachments.length === 0 && pendingFiles.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "16px 0", color: "#94A3B8", fontSize: "0.8rem" }}>
                    No attachments yet
                  </div>
                ) : (
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 10 }}>
                    {/* Pending (uploaded to staging, not yet on the invoice) */}
                    {pendingFiles.map((pf) => {
                      const filename = pf.name;
                      const t = getFileTypeFromKey(filename);
                      const canPreview = !!pf.signedUrl && !pf.uploading;
                      return (
                        <div key={pf.id} className="iv-attach-card">
                          <div
                            className="iv-attach-card__preview"
                            onClick={() => canPreview && openPendingPreview(pf)}
                            style={{ cursor: canPreview ? "zoom-in" : "default" }}
                          >
                            {pf.uploading
                              ? <Spinner size="sm" style={{ color: "#4F46E5" }} />
                              : t === "image" && pf.signedUrl
                                ? <img src={pf.signedUrl} alt={filename} />
                                : t === "pdf"
                                  ? <FeatherIcon icon="file-text" size={32} color="#DC2626" />
                                  : t === "office"
                                    ? <FeatherIcon icon="file" size={32} color="#2563EB" />
                                    : <FeatherIcon icon="file" size={32} color="#64748B" />}
                            {pf.uploading && <span className="iv-attach-card__badge">UPLOADING</span>}
                          </div>
                          <div className="iv-attach-card__name" title={filename}>{filename}</div>
                          <div className="iv-attach-card__actions">
                            {canPreview && (
                              <button
                                type="button"
                                onClick={() => openPendingPreview(pf)}
                                title="Preview"
                              >
                                <FeatherIcon icon="eye" size={12} />
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => handleRemovePending(pf.id)}
                              title="Remove"
                              style={{ color: "#DC2626" }}
                              disabled={saving || pf.uploading}
                            >
                              <FeatherIcon icon="trash-2" size={12} />
                            </button>
                          </div>
                        </div>
                      );
                    })}

                    {/* Server-confirmed attachments */}
                    {attachments.map((key) => {
                      const filename = key.split("/").pop();
                      const url = signedUrls[key];
                      const t = getFileTypeFromKey(key);
                      return (
                        <div key={key} className="iv-attach-card">
                          <div className="iv-attach-card__preview" onClick={() => url && setPreviewKey(key)}>
                            {t === "image" && url
                              ? <img src={url} alt={filename} />
                              : t === "pdf"
                                ? <FeatherIcon icon="file-text" size={32} color="#DC2626" />
                                : t === "office"
                                  ? <FeatherIcon icon="file" size={32} color="#2563EB" />
                                  : <FeatherIcon icon="file" size={32} color="#64748B" />}
                          </div>
                          <div className="iv-attach-card__name" title={filename}>{filename}</div>
                          <div className="iv-attach-card__actions">
                            <button
                              type="button"
                              onClick={() => url && setPreviewKey(key)}
                              title="Preview"
                            >
                              <FeatherIcon icon="eye" size={12} />
                            </button>
                            {url && (
                              <a href={url} target="_blank" rel="noopener noreferrer" title="Download">
                                <FeatherIcon icon="download" size={12} />
                              </a>
                            )}
                            <button
                              type="button"
                              onClick={() => handleRemove(key)}
                              title="Remove"
                              style={{ color: "#DC2626" }}
                            >
                              <FeatherIcon icon="trash-2" size={12} />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter style={{ borderTop: "1px solid #EEF2F7" }}>
          <Button
            color="secondary"
            outline
            onClick={() => handleClose(false)}
            disabled={saving || uploading}
          >
            Cancel
          </Button>
          <button
            type="button"
            className="iv-btn iv-btn--primary"
            onClick={handleSave}
            disabled={saving || uploading || pendingFiles.some((p) => p.uploading)}
          >
            {saving ? <Spinner size="sm" /> : <FeatherIcon icon="check" size={14} />}
            {isNew ? "Create Invoice" : "Save"}
          </button>
        </ModalFooter>
      </Modal>

      <FilePreviewModal
        isOpen={!!previewKey}
        toggle={() => setPreviewKey(null)}
        signedUrl={previewKey ? signedUrls[previewKey] : null}
        fileKey={previewKey}
      />

      <FilePreviewModal
        isOpen={!!previewPendingId}
        toggle={() => setPreviewPendingId(null)}
        signedUrl={previewPendingId
          ? pendingFiles.find((p) => p.id === previewPendingId)?.signedUrl || null
          : null}
        fileKey={previewPendingId
          ? pendingFiles.find((p) => p.id === previewPendingId)?.name || null
          : null}
      />
    </>
  );
};

export { InvoiceFormModal };
